import * as THREE from 'three';
import { Entity } from './entity';
import { JUMP_VELOCITY } from '../constants';
import { BlockId } from '@shared/block-ids';

export enum MobState {
  IDLE = 'idle',
  WANDER = 'wander',
  CHASE = 'chase',
  FLEE = 'flee',
}

export interface MobDrop {
  id: BlockId;
  min: number;
  max: number;
}

export interface MobDef {
  name: string;
  width: number;
  height: number;
  health: number;
  speed: number;
  hostile: boolean;
  color: number;
  bodyColor: number;
  detectionRange: number;
  attackDamage: number;
  attackRange: number;
  drops?: MobDrop[];
}

export class Mob extends Entity {
  readonly def: MobDef;
  state = MobState.IDLE;
  private stateTimer = 0;
  private targetX = 0;
  private targetZ = 0;
  private yaw = 0;

  constructor(def: MobDef, x: number, y: number, z: number) {
    super(x, y, z, def.width, def.height, def.health);
    this.def = def;
    this.buildMesh();
  }

  private buildMesh(): void {
    // Body
    const bodyGeo = new THREE.BoxGeometry(
      this.def.width * 0.9,
      this.def.height * 0.5,
      this.def.width * 1.2,
    );
    const bodyMat = new THREE.MeshLambertMaterial({ color: this.def.bodyColor });
    const bodyMesh = new THREE.Mesh(bodyGeo, bodyMat);
    bodyMesh.position.y = this.def.height * 0.4;
    this.mesh.add(bodyMesh);

    // Head
    const headSize = this.def.width * 0.7;
    const headGeo = new THREE.BoxGeometry(headSize, headSize, headSize);
    const headMat = new THREE.MeshLambertMaterial({ color: this.def.color });
    const headMesh = new THREE.Mesh(headGeo, headMat);
    headMesh.position.y = this.def.height * 0.8;
    headMesh.position.z = -this.def.width * 0.3;
    this.mesh.add(headMesh);

    // Eyes
    const eyeGeo = new THREE.BoxGeometry(0.1, 0.1, 0.05);
    const eyeMat = new THREE.MeshBasicMaterial({ color: this.def.hostile ? 0xff0000 : 0x222222 });
    const leftEye = new THREE.Mesh(eyeGeo, eyeMat);
    leftEye.position.set(-0.15, this.def.height * 0.85, -this.def.width * 0.65);
    this.mesh.add(leftEye);

    const rightEye = new THREE.Mesh(eyeGeo, eyeMat);
    rightEye.position.set(0.15, this.def.height * 0.85, -this.def.width * 0.65);
    this.mesh.add(rightEye);

    // Legs (4 for animals, 2 for humanoids)
    const legGeo = new THREE.BoxGeometry(0.2, this.def.height * 0.3, 0.2);
    const legMat = new THREE.MeshLambertMaterial({ color: this.def.bodyColor });
    const offsets = this.def.hostile
      ? [[-0.15, 0, 0], [0.15, 0, 0]] // 2 legs for humanoid
      : [[-0.2, 0, -0.3], [0.2, 0, -0.3], [-0.2, 0, 0.3], [0.2, 0, 0.3]]; // 4 legs for animal

    for (const [ox, , oz] of offsets) {
      const leg = new THREE.Mesh(legGeo, legMat);
      leg.position.set(ox, this.def.height * 0.15, oz);
      this.mesh.add(leg);
    }
  }

  update(
    dt: number,
    isSolid: (x: number, y: number, z: number) => boolean,
    playerX?: number,
    playerY?: number,
    playerZ?: number,
  ): void {
    if (this.dead) return;

    this.stateTimer -= dt;

    // State transitions
    if (playerX !== undefined && playerY !== undefined && playerZ !== undefined) {
      const dist = this.distanceTo(playerX, playerY, playerZ);

      if (this.def.hostile && dist < this.def.detectionRange) {
        this.state = MobState.CHASE;
        this.targetX = playerX;
        this.targetZ = playerZ;
      } else if (!this.def.hostile && dist < 5) {
        this.state = MobState.FLEE;
        this.targetX = this.body.x + (this.body.x - playerX);
        this.targetZ = this.body.z + (this.body.z - playerZ);
        this.stateTimer = 3;
      } else if (this.stateTimer <= 0) {
        if (Math.random() < 0.3) {
          this.state = MobState.WANDER;
          this.targetX = this.body.x + (Math.random() - 0.5) * 10;
          this.targetZ = this.body.z + (Math.random() - 0.5) * 10;
          this.stateTimer = 2 + Math.random() * 3;
        } else {
          this.state = MobState.IDLE;
          this.stateTimer = 1 + Math.random() * 3;
        }
      }
    }

    // Movement
    switch (this.state) {
      case MobState.IDLE:
        this.body.vx = 0;
        this.body.vz = 0;
        break;

      case MobState.WANDER:
      case MobState.CHASE:
      case MobState.FLEE: {
        const dx = this.targetX - this.body.x;
        const dz = this.targetZ - this.body.z;
        const dist = Math.sqrt(dx * dx + dz * dz);

        if (dist > 0.5) {
          const speed = this.state === MobState.CHASE ? this.def.speed * 1.3 :
                       this.state === MobState.FLEE ? this.def.speed * 1.5 :
                       this.def.speed;
          this.body.vx = (dx / dist) * speed;
          this.body.vz = (dz / dist) * speed;
          this.yaw = Math.atan2(dx, dz);

          // Jump if blocked
          if (this.body.onGround) {
            const frontX = Math.floor(this.body.x + (dx / dist) * 0.5);
            const frontZ = Math.floor(this.body.z + (dz / dist) * 0.5);
            const frontY = Math.floor(this.body.y);
            if (isSolid(frontX, frontY, frontZ) || isSolid(frontX, frontY + 1, frontZ)) {
              this.body.vy = JUMP_VELOCITY * 0.8;
              this.body.onGround = false;
            }
          }
        } else {
          this.body.vx = 0;
          this.body.vz = 0;
          if (this.state === MobState.WANDER) {
            this.state = MobState.IDLE;
            this.stateTimer = 1 + Math.random() * 2;
          }
        }
        break;
      }
    }

    super.update(dt, isSolid);
    this.mesh.rotation.y = this.yaw;
  }

  canAttack(playerX: number, playerY: number, playerZ: number): boolean {
    if (!this.def.hostile) return false;
    return this.distanceTo(playerX, playerY, playerZ) < this.def.attackRange;
  }

  getDrops(): { id: BlockId; count: number }[] {
    if (!this.def.drops) return [];
    return this.def.drops.map(drop => ({
      id: drop.id,
      count: drop.min + Math.floor(Math.random() * (drop.max - drop.min + 1)),
    }));
  }
}
